import { useCallback, useContext, useMemo } from "react";
import { ConditionalWrapper } from "utils/react";
import { getCapitilizedString } from "utils/strings";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { TIERS } from "constants/pricing";
import classnames from "classnames";
import SignupFormComponent from "components/core/signups/base-signup-form/signup-form-component";
import TooltipComponent from "components/enhancements/tooltip/tooltip-component";
import { DARK_THEME } from "components/enhancements/tooltip/tooltip-constants";
import Button from "components/core/button/button-component";
import { shouldShowPurchaseNow } from "services/ab-tests/monetization-ab-tests-service";
import Colors from "styles/colors";
import { BRAND_BLACK_TEXT_COLOR } from "styles/color-consts";
import { hasLearnMoreVideo, getVideoIdByLocaleAndTier } from "services/pricing-learn-more-service";
import YouTubePopUpButton from "segments/desktop/core-components/button/youtube-popup-button/youtube-popup-button";
import { SECONDARY_BUTTON_TYPE } from "segments/desktop/core-components/button/button-consts";
import { trackEvent } from "services/bigbrain-service";
import {
  HP_SOURCE,
  CONTACT_US_BUTTON_CLICKED,
  PRICING_PAGE_FEATURE_INFO_ICON_HOVER,
  PRICING_PAGE_FEATURE_INFO_ICON_EXIT,
  PRICING_PAGE_FREE_TIER_COMING_SOON_HOVER
} from "constants/bigbrain-event-types";
import { useContactSalesTypeform } from "client/services/hooks/use-contact-cales-typeform";
import { CONTACT_US_FROM_HEADER_PAGE_LINK } from "constants/links";
import { HOMEPAGE_PRICING_TIER_SOURCE } from "constants/cookies";
import { ProductIcon } from "../../../../icons/product-icon";
import { productPriceAndFeatures } from "./product-price-and-features-card.scss";
import PricingInfoIcon from "/static/svg/pricing-info-icon.svg";
import EnterpriseIcon from "/static/svg/pricing-enterprise-icon.svg";
import { ContactUsIcon } from "./contact-us-icon";
import { ProductPrice } from "./product-price/product-price";
import { withAbTests } from "/contexts/ab-tests-context";
import { shouldShowTypeformContactUs } from "/services/ab-tests/monetization-ab-tests-service";
import { getTierTranslationKey } from "/services/pricing/plans-service";
import { getDefaultContactUsPageLink } from "/services/pricing/pricing-page/contact-us-link-service";
import { setCookie } from "/services/cookies-service";

const maxNoTouchSeats = 50;
const CARD_CLASS = "card";

const ProductPriceAndFeatures = ({
  productName,
  productId,
  period,
  tier,
  previousTierName,
  color,
  primaryColor,
  secondaryColor,
  seats,
  isMostPopular,
  abTests,
  onPurchaseNowClicked,
  hideFreeForever,
  isTypeformDisabled,
  isHomepagePricing,
  showLearnMoreVideo,
  showFreeTierComingSoon
}) => {
  const { name: tierName, features } = tier;
  const { translate, forcePricing, translateWithParam, localeId } = useContext(GenericConfigContext);
  const shouldShowPrices = tierName !== TIERS.ENTERPRISE && seats < maxNoTouchSeats;
  const canPurchaseNow = shouldShowPurchaseNow({ abTests, forcePricing });
  const isFreeOrEnterprise = [TIERS.FREE, TIERS.ENTERPRISE].includes(tierName);
  const { displayTypeform } = useContactSalesTypeform({ abTests, fallbackLink: CONTACT_US_FROM_HEADER_PAGE_LINK });

  const onPurchaseNowClick = (e) => {
    const { currentTarget } = e;
    const purchaseNowLinkParent = currentTarget.closest(`div.${CARD_CLASS}`);
    const signupButton = purchaseNowLinkParent.querySelector("button");
    onPurchaseNowClicked({ tier, target: signupButton });
  };

  const tierIncludesText = useMemo(() => {
    return !previousTierName
      ? translateWithParam("pricingPage.tierCards.planIncludes", translate(getCapitilizedString(tier.name)))
      : translateWithParam("pricingPage.tierCards.includesPlanPlus", translate(previousTierName));
  }, [previousTierName, translateWithParam, tier, translate]);

  const getLearnMoreButtonText = () => {
    return translateWithParam("pricingPage.tierCards.learnMoreButton", tier.name);
  };

  const renderLearnMoreVideoCTAOrPlaceholder = () => {
    const hasVideo = hasLearnMoreVideo(tier.name);

    return (
      <div className="tier-learn-more-button">
        {hasVideo && showLearnMoreVideo && (
          <YouTubePopUpButton
            isWithIcon
            isWithIconInsteadOfArrow
            withoutUnderline
            minPadding
            outline
            isWithArrow={false}
            iconColor={color}
            buttonText={getLearnMoreButtonText()}
            videoId={getVideoIdByLocaleAndTier(localeId, tier.name)}
            buttonDisplayType={SECONDARY_BUTTON_TYPE}
            extraEventData={{ buttonName: "hp_pp_tier_learn_more", tier: tier.name, locale: localeId }}
          />
        )}
      </div>
    );
  };

  const sendHoverEvent = (feature, eventName) => {
    trackEvent(eventName, {
      info1: feature,
      info2: tier.name,
      kind: feature,
      productKind: productName
    });
  };

  const onMouseEnter = (feature) => {
    sendHoverEvent(feature, PRICING_PAGE_FEATURE_INFO_ICON_HOVER);
  };

  const onMouseLeave = (feature) => {
    sendHoverEvent(feature, PRICING_PAGE_FEATURE_INFO_ICON_EXIT);
  };

  const onMouseEnterComingSoon = (feature) => {
    trackEvent(PRICING_PAGE_FREE_TIER_COMING_SOON_HOVER, {
      info2: tier.name,
      productKind: productName
    });
  };

  const showComingSoon = useMemo(() => {
    return showFreeTierComingSoon && tierName === TIERS.FREE;
  }, [showFreeTierComingSoon, tierName]);

  const onContactUsClick = useCallback(() => {
    const shouldShowTypeform = !isTypeformDisabled && shouldShowTypeformContactUs({ abTests });
    trackEvent(CONTACT_US_BUTTON_CLICKED, {
      placement: "tier-value-card",
      source: HP_SOURCE,
      info1: tier.name,
      info2: seats,
      productKind: productName
    });
    if (shouldShowTypeform) {
      displayTypeform();
    } else {
      window.location.href = getDefaultContactUsPageLink(abTests);
    }
  }, [abTests, displayTypeform, isTypeformDisabled, productName, seats, tier.name]);

  const handleBeforeSubmitCallback = () => {
    setCookie(HOMEPAGE_PRICING_TIER_SOURCE, `${productName}-${tierName}`);
  };

  return (
    <ConditionalWrapper
      condition={isHomepagePricing}
      wrapper={(children) => (
        <div
          className={classnames("card-container", {
            "is-homepage-pricing": isHomepagePricing,
            "is-most-popular": isMostPopular
          })}
        >
          {children}
        </div>
      )}
    >
      {isHomepagePricing && isMostPopular && (
        <div className="most-popular">
          <span className="most-popular-span">{translate("pricingPage.mostPopular")}</span>
        </div>
      )}
      <div
        className={classnames(CARD_CLASS, { "is-most-popular": isMostPopular })}
        style={{ "--primary-color": primaryColor, "--secondary-color": secondaryColor }}
      >
        {!isHomepagePricing && (
          <div className="icons">
            <div className="icon" style={{ "--product-color": color }}>
              <ProductIcon product={productName} />
            </div>
          </div>
        )}
        <div className="tier-title-container">
          <div className="tier-title">
            <div className="product-and-tier">
              <div className="tier">{translate(getTierTranslationKey(tierName, { hasStudentPlan: false }))}</div>
              {isHomepagePricing && showComingSoon && (
                <div onMouseEnter={onMouseEnterComingSoon} className="coming-soon">
                  {translate("pricingPage.comingSoon")}
                </div>
              )}
              {!isHomepagePricing && productName !== "core" && (
                <div>{translate(`pricingPage.products.${productName}.title`)}</div>
              )}
            </div>
            {!isHomepagePricing && isMostPopular && (
              <div className="most-popular-badge-container">
                <div className="most-popular-badge">{translate("pricingPage.mostPopular")}</div>
              </div>
            )}
          </div>
        </div>
        {shouldShowPrices ? (
          <ProductPrice
            product={productName}
            period={period}
            seats={seats}
            tierName={tierName}
            hideFreeForever={hideFreeForever}
            splitCurrencySign={isHomepagePricing}
          />
        ) : (
          <div className="contact">{isHomepagePricing ? <EnterpriseIcon /> : <ContactUsIcon />}</div>
        )}
        <div
          className={classnames("cta-button", {
            "try-cta-padding": canPurchaseNow && isFreeOrEnterprise,
            "contact-us": !shouldShowPrices
          })}
        >
          {shouldShowPrices ? (
            <SignupFormComponent
              className="signup-form"
              ctaText={translate("signup.tryForFree")}
              buttonSize="xs"
              useCtaText
              buttonSpinnerColor={Colors[BRAND_BLACK_TEXT_COLOR]}
              pricingDetails={{ tier: tierName, userCount: seats, billingPeriod: period, product: productName }}
              signupProductId={productId}
              extraData={{ pricing_tier: tierName, cta_name: "tier_cards" }}
              beforeSubmitCallback={handleBeforeSubmitCallback}
            />
          ) : (
            <Button
              className={classnames("cta-button", "contact-us-button")}
              type="button"
              size="xs"
              onClickCallback={onContactUsClick}
            >
              {translate("footer.contactSales")}
            </Button>
          )}
        </div>
        {canPurchaseNow && !isFreeOrEnterprise && (
          <div className="pricing-signup-purchase-now">
            <span>{translate("signup.or")?.toLowerCase()}</span>
            <a className="purchase-now-link" onClick={onPurchaseNowClick}>
              {translate("pricingPage.purchaseNow")}
            </a>
          </div>
        )}
        {isHomepagePricing && renderLearnMoreVideoCTAOrPlaceholder()}
        <div className="description">
          {translate(`pricingPage.products.${productName}.tiers.${tier.name}.description`)}
        </div>
        <div className="horizontal-line" />
        {isHomepagePricing && <div className="tier-include">{tierIncludesText}</div>}
        {features.map((feature) => (
          <div className="feature">
            <span
              dangerouslySetInnerHTML={{
                __html: translate(`pricingPage.products.${productName}.tiers.${tierName}.features.${feature}`)
              }}
            />
            <div className="icon" onMouseEnter={() => onMouseEnter(feature)} onMouseLeave={() => onMouseLeave(feature)}>
              <div className="tooltip">
                <TooltipComponent theme={DARK_THEME} className="floating-tooltip">
                  {translate(`pricingPage.products.${productName}.tiers.${tierName}.features.${feature}.description`)}
                </TooltipComponent>
              </div>
              <PricingInfoIcon className="info-icon" />
            </div>
          </div>
        ))}
      </div>
      <style jsx>{productPriceAndFeatures}</style>
      <style jsx>{`
        .tier-title {
          .most-popular-badge,
          .most-popular-badge::before {
            background-color: ${isHomepagePricing ? "auto" : color};
          }
        }

        .card {
          .cta-button :global(.signup-button):hover,
          .cta-button :global(.cta-button):hover {
            border-color: ${isHomepagePricing ? "auto" : color};
            background-color: ${isHomepagePricing ? "auto" : color};
            filter: none;
          }
        }
      `}</style>
    </ConditionalWrapper>
  );
};

export default withAbTests(ProductPriceAndFeatures);
